/**
 * MetricsDashboardPage (4.3.2)
 *
 * Админ-панель метрик: Stats, Costs, Popularity, Errors.
 * Автообновление каждые 60 секунд.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import { api } from '@/api';

// ============ Types ============

interface ForecastStat {
  forecast_type: string;
  count: string;
  avg_duration_ms: string | null;
  total_ai_cost_usd: string | null;
}

interface CostDay {
  date: string;
  total_cost: string | null;
  count: string;
  avg_duration_ms: string | null;
}

interface PopularityItem {
  forecast_type: string;
  count: string;
}

interface ErrorItem {
  forecast_type: string;
  error_message: string | null;
  count: string;
  last_occurred: string;
}

interface CostTrackingData {
  currentMonthCost: number;
  costByType: Array<{ type: string; cost: number }>;
  estimatedMonthlyCost: number;
  recentCost: number;
}

// ============ Styles ============

const colors = {
  bg: '#0f172a',
  bgCard: '#1e293b',
  bgCardHover: '#334155',
  border: '#334155',
  gold: '#d4af37',
  goldLight: '#f0d060',
  goldDark: '#a08520',
  text: '#e2e8f0',
  textMuted: '#94a3b8',
  textDim: '#64748b',
  red: '#ef4444',
  redBg: '#7f1d1d',
  green: '#22c55e',
  greenBg: '#14532d',
  blue: '#3b82f6',
};

const sectionTitle: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: 700,
  color: colors.gold,
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const cardStyle: React.CSSProperties = {
  background: colors.bgCard,
  border: `1px solid ${colors.border}`,
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '24px',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse' as const,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left' as const,
  padding: '10px 12px',
  fontSize: '12px',
  fontWeight: 600,
  color: colors.textMuted,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  borderBottom: `1px solid ${colors.border}`,
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: '14px',
  color: colors.text,
  borderBottom: `1px solid ${colors.border}20`,
};

// ============ Component ============

export default function MetricsDashboardPage() {
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();

  const [stats, setStats] = useState<ForecastStat[]>([]);
  const [costs, setCosts] = useState<CostDay[]>([]);
  const [popularity, setPopularity] = useState<PopularityItem[]>([]);
  const [errors, setErrors] = useState<ErrorItem[]>([]);
  const [costTracking, setCostTracking] = useState<CostTrackingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, costsRes, popRes, errorsRes, costRes] = await Promise.all([
        api.get<{ data: ForecastStat[] }>('/api/metrics/forecasts?days=30'),
        api.get<{ data: CostDay[] }>('/api/metrics/costs?days=30'),
        api.get<{ data: PopularityItem[] }>('/api/metrics/popularity?limit=10'),
        api.get<{ data: ErrorItem[] }>('/api/metrics/errors?days=7'),
        api.get<{ data: CostTrackingData }>('/api/metrics/cost-tracking?days=30'),
      ]);

      setStats(statsRes.data?.data ?? []);
      setCosts(costsRes.data?.data ?? []);
      setPopularity(popRes.data?.data ?? []);
      setErrors(errorsRes.data?.data ?? []);
      setCostTracking(costRes.data?.data ?? null);
      setLastUpdated(new Date().toLocaleTimeString('ru-RU'));
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate, fetchData]);

  // Автообновление каждые 60 секунд
  useEffect(() => {
    const interval = setInterval(fetchData, 60_000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.gold,
        fontSize: '16px',
      }}>
        Загрузка метрик...
      </div>
    );
  }

  // Max cost for bar chart normalization
  const maxCost = Math.max(...costs.map((c) => parseFloat(c.total_cost ?? '0')), 1);

  return (
    <div style={{ minHeight: '100vh', background: colors.bg }}>
      {/* Header */}
      <header style={{
        background: '#1e293b',
        borderBottom: `1px solid ${colors.border}`,
        padding: '16px 24px',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontSize: '20px', fontWeight: 700, color: '#fff' }}>Astranum Metrics</span>
            <span style={{ fontSize: '12px', color: colors.textDim }}>
              Обновлено: {lastUpdated || '—'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={fetchData}
              style={{
                padding: '6px 14px',
                fontSize: '13px',
                background: colors.goldDark,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Обновить
            </button>
            <Link
              to="/admin/dashboard"
              style={{
                padding: '6px 14px',
                fontSize: '13px',
                background: '#334155',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                textDecoration: 'none',
              }}
            >
              Назад
            </Link>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: '1280px', margin: '0 auto', padding: '24px' }}>
        {/* Cost Tracking Summary (4.3.5) */}
        {costTracking && (
          <div style={{ ...cardStyle, marginBottom: '24px' }}>
            <h2 style={sectionTitle}>💰 Расходы</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div style={{
                background: `${colors.gold}15`,
                border: `1px solid ${colors.gold}30`,
                borderRadius: '8px',
                padding: '16px',
              }}>
                <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '4px' }}>Текущий месяц</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: colors.gold }}>
                  ${costTracking.currentMonthCost.toFixed(2)}
                </div>
              </div>
              <div style={{
                background: `${colors.gold}15`,
                border: `1px solid ${colors.gold}30`,
                borderRadius: '8px',
                padding: '16px',
              }}>
                <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '4px' }}>Прогноз на месяц</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: colors.goldLight }}>
                  ${costTracking.estimatedMonthlyCost.toFixed(2)}
                </div>
              </div>
              <div style={{
                background: `${colors.gold}15`,
                border: `1px solid ${colors.gold}30`,
                borderRadius: '8px',
                padding: '16px',
              }}>
                <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '4px' }}>За 30 дней</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: colors.gold }}>
                  ${costTracking.recentCost.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Cost by type */}
            {costTracking.costByType.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '8px' }}>По типам:</div>
                {costTracking.costByType.map((item) => (
                  <div key={item.type} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '6px',
                  }}>
                    <span style={{ width: '140px', fontSize: '13px', color: colors.text, flexShrink: 0 }}>
                      {item.type}
                    </span>
                    <div style={{ flex: 1, height: '8px', background: `${colors.gold}15`, borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min((item.cost / (costTracking.costByType[0]?.cost || 1)) * 100, 100)}%`,
                        height: '100%',
                        background: `linear-gradient(90deg, ${colors.goldDark}, ${colors.gold})`,
                        borderRadius: '4px',
                      }} />
                    </div>
                    <span style={{ fontSize: '13px', color: colors.gold, width: '60px', textAlign: 'right' }}>
                      ${item.cost.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 1. Stats Table */}
        <div style={cardStyle}>
          <h2 style={sectionTitle}>📊 Статистика по прогнозам</h2>
          {stats.length === 0 ? (
            <div style={{ color: colors.textDim, textAlign: 'center', padding: '24px' }}>Нет данных</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Тип</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Количество</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Ср. время (мс)</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Стоимость</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((row) => (
                    <tr key={row.forecast_type} style={{ transition: 'background 0.15s' }}>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '2px 8px',
                          background: `${colors.gold}15`,
                          borderRadius: '4px',
                          fontSize: '13px',
                          color: colors.goldLight,
                        }}>
                          {row.forecast_type}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {row.count}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {row.avg_duration_ms ? Math.round(parseFloat(row.avg_duration_ms)).toLocaleString() : '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: colors.gold, fontVariantNumeric: 'tabular-nums' }}>
                        ${parseFloat(row.total_ai_cost_usd ?? '0').toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 2. Costs Bar Chart (CSS) */}
        <div style={cardStyle}>
          <h2 style={sectionTitle}>📈 Расходы за 30 дней</h2>
          {costs.length === 0 ? (
            <div style={{ color: colors.textDim, textAlign: 'center', padding: '24px' }}>Нет данных</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {costs.slice().reverse().map((day) => {
                const cost = parseFloat(day.total_cost ?? '0');
                const barWidth = Math.max((cost / maxCost) * 100, 1);
                return (
                  <div
                    key={day.date}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <span style={{ width: '64px', fontSize: '12px', color: colors.textDim, flexShrink: 0, textAlign: 'right' }}>
                      {new Date(day.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
                    </span>
                    <div style={{
                      flex: 1,
                      height: '20px',
                      background: `${colors.gold}10`,
                      borderRadius: '4px',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${barWidth}%`,
                        height: '100%',
                        background: cost > 0
                          ? `linear-gradient(90deg, ${colors.goldDark}, ${colors.gold})`
                          : 'transparent',
                        borderRadius: '4px',
                        transition: 'width 0.3s',
                      }} />
                    </div>
                    <span style={{
                      width: '60px',
                      fontSize: '12px',
                      color: colors.gold,
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      ${cost.toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 3. Popularity */}
        <div style={cardStyle}>
          <h2 style={sectionTitle}>🏆 Топ популярных прогнозов</h2>
          {popularity.length === 0 ? (
            <div style={{ color: colors.textDim, textAlign: 'center', padding: '24px' }}>Нет данных</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
              {popularity.map((item, idx) => {
                const count = parseInt(item.count, 10);
                const maxCount = parseInt(popularity[0]?.count ?? '1', 10);
                const pct = (count / maxCount) * 100;
                return (
                  <div key={item.forecast_type} style={{
                    background: `${colors.gold}08`,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    padding: '14px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: idx < 3 ? colors.gold : colors.textDim,
                        color: idx < 3 ? '#000' : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 700,
                      }}>
                        {idx + 1}
                      </span>
                      <span style={{ fontSize: '14px', color: colors.text, fontWeight: 600 }}>
                        {item.forecast_type}
                      </span>
                    </div>
                    <div style={{ height: '4px', background: `${colors.gold}20`, borderRadius: '2px', overflow: 'hidden', marginBottom: '6px' }}>
                      <div style={{
                        width: `${pct}%`,
                        height: '100%',
                        background: colors.gold,
                        borderRadius: '2px',
                      }} />
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textMuted }}>
                      {count} использований
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 4. Errors */}
        <div style={cardStyle}>
          <h2 style={sectionTitle}>
            <span style={{ color: errors.length > 0 ? colors.red : colors.green }}>
              {errors.length > 0 ? '\u26A0\uFE0F' : '\u2705'}
            </span>{' '}
            Ошибки за 7 дней ({errors.length})
          </h2>
          {errors.length === 0 ? (
            <div style={{ color: colors.green, textAlign: 'center', padding: '24px' }}>
              Ошибок нет
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Тип</th>
                    <th style={thStyle}>Ошибка</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Кол-во</th>
                    <th style={thStyle}>Последний раз</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((err, idx) => (
                    <tr key={idx}>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '2px 8px',
                          background: `${colors.red}15`,
                          borderRadius: '4px',
                          fontSize: '13px',
                          color: '#fca5a5',
                        }}>
                          {err.forecast_type}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, color: colors.red, fontSize: '13px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {err.error_message || '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: colors.red, fontVariantNumeric: 'tabular-nums' }}>
                        {err.count}
                      </td>
                      <td style={{ ...tdStyle, color: colors.textMuted, fontSize: '13px' }}>
                        {new Date(err.last_occurred).toLocaleString('ru-RU')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
